
import { useState } from 'react';

interface Hotspot {
  id: number;
  x: number;
  y: number;
  label: string;
  description: string;
}

const hotspots: Hotspot[] = [
  {
    id: 1,
    x: 52,
    y: 18,
    label: '智能控制面板',
    description: '觸控式操作介面，即時顯示訓練數據與生命體徵，支援多語言操作',
  },
  {
    id: 2,
    x: 72,
    y: 42,
    label: '電動輔助驅動系統',
    description: '精密伺服馬達提供精準阻力調節，0.1kg 級別微調，適應不同復康階段',
  },
  {
    id: 3,
    x: 30,
    y: 55,
    label: '壓力感應座椅',
    description: '多點壓力感應技術，實時監測坐姿平衡，預防壓瘡及姿勢性問題',
  },
  {
    id: 4,
    x: 60,
    y: 72,
    label: '防滑穩定底盤',
    description: '醫療級鋁合金底盤，360° 全向輪設計，確保訓練過程絕對安全穩定',
  },
  {
    id: 5,
    x: 20,
    y: 30,
    label: '無線數據傳輸',
    description: '即時將訓練數據同步至院舍管理系統，治療師可遠端監控每位院友進度',
  },
];

const specs = [
  { icon: 'ri-shield-check-line', value: 'CE 認證', label: '歐盟醫療器械認證' },
  { icon: 'ri-timer-line', value: '< 2 分鐘', label: '快速設置時間' },
  { icon: 'ri-user-heart-line', value: '98.6%', label: '院友滿意度' },
  { icon: 'ri-bar-chart-line', value: '40%', label: '復康效率提升' },
];

export default function TechShowcase() {
  const [activeHotspot, setActiveHotspot] = useState<number | null>(null);

  const handleHotspotClick = (id: number) => {
    setActiveHotspot(activeHotspot === id ? null : id);
  };

  const activeData = hotspots.find((h) => h.id === activeHotspot);

  return (
    <section className="py-32 bg-white overflow-hidden">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-20 items-start mb-20">
          <div>
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-8 h-px bg-orange-500"></div>
              <span className="text-xs font-bold tracking-[0.25em] text-orange-500 uppercase">
                Technology
              </span>
            </div>
            <h2 className="text-5xl font-bold text-slate-900 leading-tight tracking-tight">
              精準介入
              <br />
              <span className="text-orange-500">的藝術</span>
            </h2>
          </div>
          <div className="pt-4">
            <p className="text-slate-500 text-lg leading-relaxed">
              每一台器材，都是一份對復康科學的承諾。我們精選全球頂尖醫療級設備，
              以工程師的嚴謹與治療師的溫度，為每位院友打造最精準的復康方案。
            </p>
            <div className="mt-8 flex items-center gap-6">
              <a
                href="/services/equipment-monitoring"
                className="inline-flex items-center gap-2 text-slate-900 font-bold text-sm tracking-wide hover:text-orange-500 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                探索完整器材目錄
                <i className="ri-arrow-right-line text-base"></i>
              </a>
              <div className="w-px h-5 bg-slate-200"></div>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 text-orange-500 font-bold text-sm tracking-wide hover:text-orange-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                預約示範
                <i className="ri-calendar-line text-base"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Main 2-Column Layout */}
        <div className="grid grid-cols-2 gap-16 items-center">
          {/* LEFT — Image with Hotspots */}
          <div className="relative">
            {/* Glow backdrop */}
            <div className="absolute -inset-8 bg-gradient-to-br from-orange-100/60 via-orange-50/30 to-transparent rounded-[3rem] blur-2xl"></div>

            {/* Image container */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 shadow-[0_32px_80px_rgba(0,0,0,0.12)]">
              <div className="w-full h-[560px] relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/e2e767ab87fe63f41363e2eeb3fb4b02.jpg"
                  alt="智能復康器材"
                  className="w-full h-full object-cover object-center"
                />
                {/* Subtle overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 via-transparent to-white/10 pointer-events-none"></div>

                {/* Hotspot Dots */}
                {hotspots.map((spot) => (
                  <button
                    key={spot.id}
                    onClick={() => handleHotspotClick(spot.id)}
                    className="absolute cursor-pointer group"
                    style={{ left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)' }}
                    aria-label={spot.label}
                  >
                    {/* Outer pulse ring */}
                    <span
                      className={`absolute inset-0 rounded-full transition-all duration-300 ${
                        activeHotspot === spot.id
                          ? 'bg-orange-500/30 scale-[2.8]'
                          : 'bg-orange-400/20 scale-[2.2] animate-ping'
                      }`}
                    ></span>
                    {/* Middle ring */}
                    <span
                      className={`absolute inset-0 rounded-full transition-all duration-300 ${
                        activeHotspot === spot.id
                          ? 'bg-orange-500/50 scale-[1.8]'
                          : 'bg-orange-400/30 scale-[1.6]'
                      }`}
                    ></span>
                    {/* Core dot */}
                    <span
                      className={`relative flex w-4 h-4 rounded-full transition-all duration-300 shadow-lg ${
                        activeHotspot === spot.id
                          ? 'bg-orange-500 scale-125 shadow-orange-400/60'
                          : 'bg-orange-500 group-hover:scale-110 shadow-orange-400/40'
                      }`}
                    ></span>
                  </button>
                ))}
              </div>
            </div>

            {/* Instruction hint */}
            <div className="mt-5 flex items-center gap-2 justify-center">
              <div className="w-3 h-3 flex items-center justify-center">
                <span className="block w-2 h-2 rounded-full bg-orange-400 animate-ping"></span>
              </div>
              <p className="text-xs text-slate-400 tracking-wide">點擊橙色標記，探索器材核心技術</p>
            </div>
          </div>

          {/* RIGHT — Info Panel */}
          <div className="space-y-8">
            {/* Active Hotspot Detail Card */}
            <div
              className={`transition-all duration-500 ${
                activeData
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4 pointer-events-none'
              }`}
            >
              {activeData && (
                <div className="relative bg-slate-900 rounded-2xl p-8 overflow-hidden">
                  {/* Decorative line */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-600 rounded-l-2xl"></div>
                  {/* Glow */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded-xl flex-shrink-0">
                      <i className="ri-focus-3-line text-orange-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold tracking-[0.2em] text-orange-400 uppercase mb-2">
                        技術亮點 · 0{activeData.id}
                      </p>
                      <h3 className="text-xl font-bold text-white mb-3">{activeData.label}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed">{activeData.description}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Default state when no hotspot selected */}
            {!activeData && (
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-cursor-line text-orange-500"></i>
                  </div>
                  <p className="text-sm font-semibold text-slate-700">互動技術圖解</p>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  點擊左側圖片上的橙色標記，即可深入了解每個核心技術組件的功能與優勢。
                </p>
              </div>
            )}

            {/* Hotspot List */}
            <div className="space-y-3">
              {hotspots.map((spot) => (
                <button
                  key={spot.id}
                  onClick={() => handleHotspotClick(spot.id)}
                  className={`w-full text-left flex items-center gap-4 px-5 py-4 rounded-xl border transition-all duration-300 cursor-pointer group ${
                    activeHotspot === spot.id
                      ? 'bg-orange-50 border-orange-300 shadow-sm'
                      : 'bg-white border-slate-100 hover:border-orange-200 hover:bg-orange-50/50'
                  }`}
                >
                  {/* Line connector */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 transition-all duration-300 ${
                        activeHotspot === spot.id ? 'bg-orange-500 scale-125' : 'bg-slate-300 group-hover:bg-orange-400'
                      }`}
                    ></span>
                    <div
                      className={`w-8 h-px transition-all duration-300 ${
                        activeHotspot === spot.id ? 'bg-orange-400' : 'bg-slate-200 group-hover:bg-orange-300'
                      }`}
                    ></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-semibold transition-colors duration-300 ${
                        activeHotspot === spot.id ? 'text-orange-600' : 'text-slate-700 group-hover:text-slate-900'
                      }`}
                    >
                      {spot.label}
                    </p>
                  </div>
                  <span className="text-xs text-slate-300 font-mono flex-shrink-0">0{spot.id}</span>
                </button>
              ))}
            </div>

            {/* Specs Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
              {specs.map((spec, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg flex-shrink-0 mt-0.5">
                    <i className={`${spec.icon} text-orange-500 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-900 leading-tight">{spec.value}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{spec.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
